import { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, AlertCircle, LogIn, ArrowLeft } from 'lucide-react';
import { getApiBase } from './apiBase';
import OrcaLogo from './OrcaLogo';

const API = axios.create({
  baseURL: getApiBase(),
  withCredentials: true,
  timeout: 20000,
});

function readLoginErrorFromUrl() {
  try {
    const params = new URLSearchParams(window.location.search);
    const err = params.get('loginError');
    if (err) {
      window.history.replaceState({}, '', window.location.pathname);
      return decodeURIComponent(err);
    }
  } catch {
    // ignore
  }
  return '';
}

export default function Login({ product, onSuccess, onBack }) {
  const [mode, setMode] = useState('login');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(readLoginErrorFromUrl);
  const [workos, setWorkos] = useState(null);

  useEffect(() => {
    API.get('/auth/workos/config', { validateStatus: () => true })
      .then((res) => {
        if (res.status === 200) setWorkos(res.data);
      })
      .catch(() => setWorkos(null));
  }, []);

  const workosReady = workos?.workosEnabled === true;
  const showLocalAuth = !workosReady || workos?.localAuthEnabled !== false;

  function startWorkosLogin(switchAccount = false) {
    const params = new URLSearchParams(window.location.search);
    const useSwitch =
      switchAccount || params.get('signedOut') === '1' || params.get('switch') === '1';
    const qs = useSwitch ? '?switch=1' : '';
    window.location.href = `${getApiBase()}/auth/workos/login${qs}`;
  }

  async function submit(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const url = mode === 'register' ? '/auth/register' : '/auth/login';
      const res = await API.post(url, { username: username.trim(), password });
      const signedIn = res.data?.user;
      if (!signedIn) {
        setError('Sign-in failed. Please try again.');
        return;
      }
      // Login already created the session. Do not fail the user if a follow-up
      // /auth/me check races the cookie — use the signed-in user from login.
      onSuccess(signedIn);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Sign-in failed');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-orca-base p-4">
      <div className="w-full max-w-sm rounded-2xl bg-orca-panel p-6 shadow-xl border border-orca">
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            className="mb-4 inline-flex items-center gap-1.5 text-xs font-medium text-orca-muted hover:text-orca-nav-hover"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Back to features
          </button>
        )}
        <div className="mb-5 flex flex-col items-center">
          <OrcaLogo className="h-14 w-14 rounded-xl" />
          <h1 className="mt-3 text-xl font-semibold text-orca-heading">{product?.name || 'Orca AI'}</h1>
          {product?.tagline && (
            <p className="mt-1 text-sm text-orca-muted">{product.tagline}</p>
          )}
        </div>

        {error && (
          <div className="mb-3 flex items-start gap-2 rounded-lg bg-orca-raised border border-rose-900/50 p-2 text-sm text-rose-300">
            <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
            <div className="break-words">{error}</div>
          </div>
        )}

        {workosReady && (
          <div className="mb-4 space-y-3">
            <button
              type="button"
              onClick={startWorkosLogin}
              className="w-full flex items-center justify-center gap-2 rounded-lg border border-orca bg-orca-raised px-4 py-2.5 text-sm font-medium text-orca-heading hover:brightness-110"
            >
              <LogIn className="h-4 w-4" />
              Sign in with SSO
            </button>
            {showLocalAuth && (
              <p className="text-center text-xs text-orca-muted">or use a local account</p>
            )}
          </div>
        )}

        {showLocalAuth && (
          <>
            <div className="mb-4 grid grid-cols-2 gap-1 rounded-lg bg-orca-raised p-1">
              <button
                type="button"
                onClick={() => setMode('login')}
                className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                  mode === 'login' ? 'bg-orca-panel text-orca-heading shadow-sm' : 'text-orca-muted hover:text-orca-nav-hover'
                }`}
              >
                Sign in
              </button>
              <button
                type="button"
                onClick={() => setMode('register')}
                className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                  mode === 'register' ? 'bg-orca-panel text-orca-heading shadow-sm' : 'text-orca-muted hover:text-orca-nav-hover'
                }`}
              >
                Create account
              </button>
            </div>

            <form onSubmit={submit} className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-orca-muted mb-1">Username</label>
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  autoFocus={!workosReady}
                  required
                  minLength={2}
                  className="w-full rounded-lg border border-orca bg-orca-raised px-3 py-2 text-sm text-orca-text focus:border-cyan-500 focus:outline-none focus:ring-1 focus:ring-cyan-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-orca-muted mb-1">Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={mode === 'register' ? 6 : 1}
                  className="w-full rounded-lg border border-orca bg-orca-raised px-3 py-2 text-sm text-orca-text focus:border-cyan-500 focus:outline-none focus:ring-1 focus:ring-cyan-500"
                />
              </div>
              <button
                type="submit"
                disabled={loading || !username || !password}
                className="w-full flex items-center justify-center gap-2 rounded-lg bg-cyan-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-cyan-500 disabled:bg-orca-border"
              >
                {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                {mode === 'register' ? 'Create account' : 'Sign in'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
